import React from 'react';
import type RapidBoardType from '@jira/spa-apps__rapid-board/src/RapidBoardWrapper.tsx';
import type RapidBoardConfigApiType from '@jira/router-resources__classic-projects/src/RapidBoardConfigApiType.tsx';
import type RapidBoardDataApiType from '@jira/router-resources__classic-projects/src/RapidBoardDataApiType.tsx';
import type RapidBoardResourceType from '@jira/router-resources__classic-projects/src/RapidBoardResourceType.tsx';
import type RapidBoardWrmBundleResourceType from '@jira/router-resources__classic-projects/src/RapidBoardWrmBundleResourceType.tsx';
import type { BacklogMetaAPIConsumerWrapperQuery } from '@jira/spa-apps__next-gen-backlog/src/__generated__/BacklogMetaAPIConsumerWrapperQuery.graphql.ts';
import type { EntryPointProps } from 'react-relay';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { lazyForPaint, type LazyComponent } from '@atlassian/react-loosely-lazy';
import type {
	ResourceStoreContext,
	RouteContext,
	RouterDataContext,
} from '@atlassian/react-resource-router';

export const UIFBoardSpa: LazyComponent<React.FC<{}>> = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-uif-board", jiraSpaEntry: "async-uif-board" */ '@jira/spa-apps__next-gen-board/src/BoardSPAApp.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

export const UIFBacklogSpa: LazyComponent<
	(
		props: EntryPointProps<
			{
				backlogDataQuery: BacklogMetaAPIConsumerWrapperQuery;
			},
			{},
			{},
			{}
		>,
	) => React.JSX.Element
> = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-uif-backlog", jiraSpaEntry: "async-uif-backlog" */ '@jira/spa-apps__next-gen-backlog/src/BacklogWrapper.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

export const BoardSettingsSpa: LazyComponent<() => React.JSX.Element> = lazyForPaint(
	() =>
		// eslint-disable-next-line jira/import/file-extension-required
		import(
			/* webpackChunkName: "async-uif-board-settings", jiraSpaEntry: "async-uif-board-settings" */ '@jira/router-routes__software-classic-board-settings-switcher'
		).then((module) => module.BoardConfigSwitcher),
	{ entryFsSsrLiftImportToModule: true },
);

/**
 * Route page component for Jira Software RapidBoard CMP boards and backlogs, legacy and UIF.
 */
export const RapidBoardSpaPage = (props: RouteContext): React.JSX.Element => {
	const { route, query, match } = props;
	const basePageId = (route && route.name) || 'rapid-board';
	const chartParam = query.chart ? `-${query.chart}` : '';
	const boardId = query.rapidView ?? match.params.boardId;
	const pageId = `${basePageId}-${boardId}${chartParam}`;

	if (
		['rapidboard-board', 'rapidboard-board-embed', 'rapidboard-user-board'].includes(route.name) &&
		!query.config
	) {
		return <LazyPage Page={UIFBoardSpa} pageId={pageId} shouldShowSpinner={false} />;
	}

	if (
		['rapidboard-backlog', 'rapidboard-user-backlog', 'rapidboard-backlog-embed'].includes(
			route.name,
		) &&
		!query.config
	) {
		return <LazyPage Page={UIFBacklogSpa} pageId={pageId} shouldShowSpinner={false} />;
	}

	if (
		[
			'rapidboard-backlog',
			'rapidboard-user-backlog',
			'rapidboard-board',
			'rapidboard-board-embed',
			'rapidboard-user-board',
			'rapidboard-backlog-embed',
		].includes(route.name) &&
		query.config
	) {
		return <LazyPage Page={BoardSettingsSpa} pageId={pageId} shouldShowSpinner={false} />;
	}

	return <LazyPage Page={LazyRapidBoard} pageId={pageId} shouldShowSpinner={false} />;
};

export const LazyRapidBoard: LazyComponent<
	(_props: EntryPointProps<{}, {}, {}, {}>) => React.JSX.Element
> = lazyForPaint<typeof RapidBoardType>(
	() =>
		import(
			/* webpackChunkName: "async-rapid-board", jiraSpaEntry: "async-rapid-board" */ '@jira/spa-apps__rapid-board/src/RapidBoardWithHistory.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

export const LazyRapidBoardWrmBundleResource: LazyComponent<
	// @ts-expect-error - TS2344: generic constraint not satisfied (iso-decl artefact)

	// oxlint-disable-next-line typescript/no-explicit-any
	({ match, route }: RouterDataContext, _storeContext: ResourceStoreContext) => Promise<any>
	// @ts-expect-error - TS2344: generic constraint not satisfied (iso-decl artefact)
> = lazyForPaint<typeof RapidBoardWrmBundleResourceType>(
	() =>
		import(
			/* webpackChunkName: "async-resource-rapidboards-wrm-bundle", jiraSpaEntry: "async-resource-rapidboards-wrm-bundle" */ '@jira/router-resources__classic-projects/src/RapidBoardWrmBundleResourceType.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

export const LazyRapidBoardDataApiResource: LazyComponent<
	// @ts-expect-error - TS2344: generic constraint not satisfied (iso-decl artefact)
	(
		{ query, route, match }: RouterDataContext,
		_storeContext: ResourceStoreContext,
	) => Promise<{
		// oxlint-disable-next-line typescript/no-explicit-any
		data: Promise<any>;
		route: string;
		allDataTraceId: string | undefined;
	} | null>
	// @ts-expect-error - TS2344: generic constraint not satisfied (iso-decl artefact)
> = lazyForPaint<typeof RapidBoardDataApiType>(
	() =>
		import(
			/* webpackChunkName: "async-resource-rapidboards-data-api", jiraSpaEntry: "async-resource-rapidboards-data-api" */ '@jira/router-resources__classic-projects/src/RapidBoardDataApiType.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

export const LazyRapidBoardConfigApiResource: LazyComponent<
	// @ts-expect-error - TS2344: generic constraint not satisfied (iso-decl artefact)
	(
		{ query, route, match }: RouterDataContext,
		_storeContext: ResourceStoreContext,
	) => Promise<{
		// oxlint-disable-next-line typescript/no-explicit-any
		data: any;
		boardId: string;
		route: string;
	}>
	// @ts-expect-error - TS2344: generic constraint not satisfied (iso-decl artefact)
> = lazyForPaint<typeof RapidBoardConfigApiType>(
	() =>
		import(
			/* webpackChunkName: "async-resource-rapidboards-config-api", jiraSpaEntry: "async-resource-rapidboards-config-api" */ '@jira/router-resources__classic-projects/src/RapidBoardConfigApiType.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

export const LazyRapidBoardResource: LazyComponent<
	// @ts-expect-error - TS2344: generic constraint not satisfied (iso-decl artefact)

	// oxlint-disable-next-line typescript/no-explicit-any
	({ query, match }: RouterDataContext, _storeContext: ResourceStoreContext) => Promise<any>
	// @ts-expect-error - TS2344: generic constraint not satisfied (iso-decl artefact)
> = lazyForPaint<typeof RapidBoardResourceType>(
	() =>
		import(
			/* webpackChunkName: "async-resource-rapidboards-rapidboard-resource-data", jiraSpaEntry: "async-resource-rapidboards-rapidboard-resource-data" */ '@jira/router-resources__classic-projects/src/RapidBoardResourceType.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

/**
 * @hack This is not a component, but it needs to be preloaded.
 * Rapid boards imports from /entry because they cannot move the app install logic while rapidboard lives inside and outside the SPA
 * This hack should be removed when they finish with the migration
 */

// oxlint-disable-next-line typescript/no-explicit-any
export const LazyRapidBoardApps: LazyComponent<any> = lazyForPaint<any>(
	() =>
		import(
			/* webpackChunkName: "async-rapid-board-apps" */ '@jira/software__entry-rapid-board-wrm-apps/src/startApps.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);
